<template>
  <v-dialog :value="value" :max-width="maxWidth" :persistent="persistent" content-class="app-modal-dialog"
    @input="onDialogInput">
    <div class="app-modal" :class="contentClass">
      <header class="app-modal__header">
        <div class="app-modal__title-row">
          <slot name="title-icon" />
          <slot name="title">
            <h2 class="app-modal__title">{{ title }}</h2>
          </slot>
          <slot name="title-actions" />
        </div>
        <button v-if="showCloseButton" type="button" class="app-modal__close" aria-label="Close" @click="close">
          <i class="ri-close-line"></i>
        </button>
      </header>

      <div v-if="hasDescription" class="app-modal__description">
        <slot name="description">{{ description }}</slot>
      </div>

      <div v-if="$slots.default" class="app-modal__body">
        <slot />
      </div>

      <footer v-if="!hideFooter && $slots.footer" class="app-modal__footer">
        <slot name="footer" />
      </footer>
    </div>
  </v-dialog>
</template>

<script>
const SIZE_WIDTH_MAP = {
  sm: 384,
  md: 448,
  lg: 512,
  xl: 576,
  full: '95vw',
};

export default {
  name: 'AppModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => Object.keys(SIZE_WIDTH_MAP).includes(value),
    },
    width: {
      type: [Number, String],
      default: null,
    },
    contentClass: {
      type: String,
      default: '',
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    maxWidth() {
      return this.width || SIZE_WIDTH_MAP[this.size];
    },
    hasDescription() {
      return Boolean(this.description) || Boolean(this.$slots.description);
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
      this.$emit('close');
    },
    onDialogInput(isOpen) {
      this.$emit('input', isOpen);
      if (!isOpen) {
        this.$emit('close');
      }
    },
  },
};
</script>

<style scoped>
.app-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

.app-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #ededed;
  box-shadow: none;
}

.app-modal__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.app-modal__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 150%;
  color: #4c4c4c;
}

.app-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666666;
  cursor: pointer;
  flex-shrink: 0;
}

.app-modal__close:hover {
  background: #f5f5f5;
}

.app-modal__close i {
  font-size: 20px;
}

.app-modal__description {
  padding: 0 24px;
  margin-top: 16px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  color: #666666;
}

.app-modal__body {
  padding: 16px 24px;
}

.app-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px 16px;
  margin-top: 16px;
}
</style>

<style>
.app-modal-dialog {
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(102, 102, 102, 0.16) !important;
  overflow: hidden;
}
</style>
